/* What Lumiq observed per dataset, against one framework's controls.
 *
 * **The band above the matrix is required and not collapsible.** It is the sentence that stops a
 * reader taking a row of green chips for a compliance judgement, and it carries the server's own
 * wording rather than a copy of it — the contract makes `not_a_compliance_verdict` required for
 * exactly this reason, and restating it here would be a second place deciding what the platform
 * claims.
 *
 * **A failed read renders no matrix at all.** An empty grid reads as "nothing is covered", which is a
 * claim about the estate rather than about the request — the same inversion `build_sections` refuses
 * for a pack, and the reason the volume monitor treats an unmeasurable reading as breaking its run.
 *
 * **The worklist is the actionable half of the page**, and it is why this screen is worth more than a
 * green tick: a product asserting satisfaction cannot publish its own blind spots without undermining
 * itself, and this one is made of them.
 */

import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import type { ControlCoverage, Framework } from '@/shared/api/generated/compliance';
import { SkeletonRows } from '@/shared/ui/Skeleton';

import { useCoverage } from '../api/compliance';
import {
  cellDetail,
  COVERAGE_TONE,
  coverageLabelKey,
  worklistFrom,
} from '../model/coverage';

function Cell({ control }: { readonly control: ControlCoverage }): ReactElement {
  const { t } = useTranslation();
  const tone = COVERAGE_TONE[control.state];
  const detail = cellDetail(control);

  return (
    <span
      className={
        tone.italic
          ? `lq-tag lq-tag--cov-${tone.tone} lq-cv__cell--estate`
          : `lq-tag lq-tag--cov-${tone.tone}`
      }
      title={t(detail.key, {
        observed: detail.observed,
        missing: detail.missing,
        estate: detail.estate,
      })}
    >
      <span className="lq-tag__dot" aria-hidden="true" />
      {t(coverageLabelKey(control.state))}
    </span>
  );
}

export function CoverageByDataset({
  framework,
}: {
  readonly framework: Framework;
}): ReactElement {
  const { t } = useTranslation();
  const coverage = useCoverage(framework.code, framework.version);

  const rows = coverage.isSuccess ? coverage.data.items : [];
  const worklist = worklistFrom(rows);

  return (
    <>
      <div className="lq-sect">
        <h2 className="lq-sect__title">{t('compliance.coverage.section')}</h2>
      </div>

      {/* Always, and above everything — including the skeleton. A reader who arrives while the read
          is in flight must see it before the first chip appears. */}
      <div className="lq-band lq-band--warn lq-band--closing">
        <div className="lq-band__title">{t('compliance.coverage.notAVerdict')}</div>
        <div className="lq-band__body">
          {coverage.isSuccess
            ? coverage.data.disclaimer
            : t('compliance.coverage.notAVerdictBody')}
        </div>
      </div>

      {coverage.isPending && <SkeletonRows rows={4} columns={4} />}

      {coverage.isError && (
        /* An error card rather than an empty matrix, and the copy says why the matrix is absent. */
        <div className="lq-band lq-band--error lq-band--closing" role="status">
          <div className="lq-band__title">{t('compliance.coverage.failed')}</div>
          <div className="lq-band__body">{t('compliance.coverage.failedBody')}</div>
        </div>
      )}

      {coverage.isSuccess && rows.length === 0 && (
        <div className="lq-cp__none">
          <div className="lq-cp__noneTitle">{t('compliance.coverage.empty')}</div>
          <div className="lq-cp__noneBody">{t('compliance.coverage.emptyBody')}</div>
        </div>
      )}

      {coverage.isSuccess && rows.length > 0 && (
        <div className="lq-cv">
          <div className="lq-cv__matrix">
            <div className="lq-table-scroll lq-table-scroll--framed">
              <table className="lq-table lq-table--ledger">
                <caption className="lq-visually-hidden">
                  {t('compliance.coverage.caption', { framework: framework.title })}
                </caption>
                <thead>
                  <tr>
                    {/* Sticky, so the dataset name stays readable while a wide framework scrolls.
                        A matrix whose row label scrolls away is a grid of chips about nothing. */}
                    <th scope="col" className="lq-cv__sticky">
                      {t('compliance.coverage.dataset')}
                    </th>
                    {framework.controls.map((control) => (
                      <th key={control.code} scope="col" className="lq-nowrap">
                        <span className="lq-cv__code lq-mono" title={control.title}>
                          {control.code}
                        </span>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => (
                    <tr key={row.dataset_id} className="lq-cp__packRow">
                      <td className="lq-cv__sticky lq-mono lq-nowrap">
                        {row.qualified_name}
                      </td>
                      {row.controls.map((control) => (
                        <td key={control.control_code}>
                          <Cell control={control} />
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="lq-note">{t('compliance.coverage.estateNote')}</p>
            {coverage.data.truncated && (
              /* Reported, never inferred from a full page: a cap whose truncation is invisible reads
                 as completeness, which here would be a claim about an estate nobody examined. */
              <p className="lq-note">
                {t('compliance.coverage.truncated', { shown: rows.length })}
              </p>
            )}
          </div>

          <section className="lq-cv__worklist">
            <h3 className="lq-cp__mapHead">{t('compliance.coverage.worklist')}</h3>
            {worklist.length === 0 ? (
              <p className="lq-cp__panelBody">{t('compliance.coverage.worklistEmpty')}</p>
            ) : (
              worklist.map((entry) => (
                <div key={entry.dataset} className="lq-cv__task">
                  <div className="lq-cv__taskHead">
                    <span className="lq-cv__taskName lq-mono">{entry.dataset}</span>
                    <span className="lq-cv__taskCount">
                      {t('compliance.coverage.blankControls', {
                        count: entry.blankControls,
                      })}
                    </span>
                  </div>
                  <div className="lq-cp__kinds">
                    {entry.missing.map((kind) => (
                      <span
                        key={kind}
                        className="lq-cp__kind lq-cp__kind--small lq-mono"
                        title={t(`compliance.evidence.${kind}`)}
                      >
                        {kind}
                      </span>
                    ))}
                  </div>
                </div>
              ))
            )}
            {/* The reference's closing line, and it is the sharpest sentence on the page: collecting
                the evidence is the work, and deciding whether the control is met is not Lumiq's. */}
            {worklist.length > 0 && (
              <p className="lq-note">{t('compliance.coverage.worklistFoot')}</p>
            )}
          </section>
        </div>
      )}
    </>
  );
}
